import React from 'react';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon, Info as InfoIcon } from '@mui/icons-material';

interface SimilarQuestion {
  _id: string;
  question: string;
  answer: string;
  category: string;
  type: 'objection' | 'faq';
  isOriginal: boolean;
  parentId?: string;
  parentQuestion?: string;
  variations: Array<{ _id: string; question: string }>;
  similarity: number;
  similarityDetails: {
    levenshtein: number;
    ngram: number;
    token: number;
    wordOrder: number;
  };
  matchType: string;
  confidenceLevel: 'high' | 'medium' | 'low';
}

interface SimilarityCheckStepProps {
  similarQuestions: SimilarQuestion[];
  newQuestion: string;
  editedQuestion?: string;
  onQuestionChange?: (question: string) => void;
  onUseExisting: (question: SimilarQuestion) => void;
  onAddAsVariation: (parentQuestion: SimilarQuestion) => void;
  onAddAsNew: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}

const SimilarityCheckStep: React.FC<SimilarityCheckStepProps> = ({
  similarQuestions,
  newQuestion,
  editedQuestion,
  onQuestionChange,
  onUseExisting,
  onAddAsVariation,
  onAddAsNew,
  onCancel,
  isProcessing
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [localEditedQuestion, setLocalEditedQuestion] = React.useState(editedQuestion || newQuestion);
  const getConfidenceIcon = (level: string) => {
    switch (level) {
      case 'high':
        return <WarningIcon className="text-red-500" style={{ fontSize: '1.25rem' }} />;
      case 'medium':
        return <InfoIcon className="text-yellow-500" style={{ fontSize: '1.25rem' }} />;
      case 'low':
        return <CheckCircleIcon className="text-blue-500" style={{ fontSize: '1.25rem' }} />;
      default:
        return null;
    }
  };

  const getConfidenceMessage = (level: string, similarity: number) => {
    const percentage = Math.round(similarity * 100);
    switch (level) {
      case 'high':
        return `${percentage}% match - This appears to be a duplicate`;
      case 'medium':
        return `${percentage}% match - This might be a variation`;
      case 'low':
        return `${percentage}% match - Related but different`;
      default:
        return `${percentage}% match`;
    }
  };

  const getSimilarityBadgeColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Group questions by confidence level
  const highConfidence = similarQuestions.filter(q => q.confidenceLevel === 'high');
  const mediumConfidence = similarQuestions.filter(q => q.confidenceLevel === 'medium');
  const lowConfidence = similarQuestions.filter(q => q.confidenceLevel === 'low');

  return (
    <div className="space-y-4">
      {/* Header Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoIcon className="text-blue-600 mt-0.5" style={{ fontSize: '1.25rem' }} />
          <div>
            <p className="text-sm font-medium text-blue-900">
              Similar questions found in the database
            </p>
            <p className="text-sm text-blue-700 mt-1">
              We found {similarQuestions.length} similar question{similarQuestions.length !== 1 ? 's' : ''}.
              You can either use an existing one or add your question as new.
            </p>
          </div>
        </div>
      </div>

      {/* New Question Display/Edit */}
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-medium text-gray-500">Your new question:</p>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              ✏️ Edit
            </button>
          )}
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={localEditedQuestion}
              onChange={(e) => {
                setLocalEditedQuestion(e.target.value);
                if (onQuestionChange) {
                  onQuestionChange(e.target.value);
                }
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setIsEditing(false);
                  if (onQuestionChange && localEditedQuestion !== (editedQuestion || newQuestion)) {
                    onQuestionChange(localEditedQuestion);
                  }
                }}
                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setLocalEditedQuestion(editedQuestion || newQuestion);
                  setIsEditing(false);
                }}
                className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-900">{localEditedQuestion}</p>
        )}
      </div>

      {/* Similar Questions List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {/* High Confidence Matches */}
        {highConfidence.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-red-700 flex items-center gap-2">
              <WarningIcon className="text-red-500" style={{ fontSize: '1rem' }} />
              Likely Duplicates
            </h4>
            {highConfidence.map((question) => (
              <QuestionCard
                key={question._id}
                question={question}
                onUseExisting={onUseExisting}
                onAddAsVariation={onAddAsVariation}
                isProcessing={isProcessing}
                getConfidenceIcon={getConfidenceIcon}
                getConfidenceMessage={getConfidenceMessage}
                getSimilarityBadgeColor={getSimilarityBadgeColor}
              />
            ))}
          </div>
        )}

        {/* Medium Confidence Matches */}
        {mediumConfidence.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-yellow-700 flex items-center gap-2">
              <InfoIcon className="text-yellow-500" style={{ fontSize: '1rem' }} />
              Possible Variations
            </h4>
            {mediumConfidence.map((question) => (
              <QuestionCard
                key={question._id}
                question={question}
                onUseExisting={onUseExisting}
                onAddAsVariation={onAddAsVariation}
                isProcessing={isProcessing}
                getConfidenceIcon={getConfidenceIcon}
                getConfidenceMessage={getConfidenceMessage}
                getSimilarityBadgeColor={getSimilarityBadgeColor}
              />
            ))}
          </div>
        )}

        {/* Low Confidence Matches */}
        {lowConfidence.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-blue-700 flex items-center gap-2">
              <CheckCircleIcon className="text-blue-500" style={{ fontSize: '1rem' }} />
              Related Questions
            </h4>
            {lowConfidence.map((question) => (
              <QuestionCard
                key={question._id}
                question={question}
                onUseExisting={onUseExisting}
                onAddAsVariation={onAddAsVariation}
                isProcessing={isProcessing}
                getConfidenceIcon={getConfidenceIcon}
                getConfidenceMessage={getConfidenceMessage}
                getSimilarityBadgeColor={getSimilarityBadgeColor}
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <button
          onClick={onCancel}
          disabled={isProcessing}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onAddAsNew}
          disabled={isProcessing}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add as New Original Question
        </button>
      </div>
    </div>
  );
};

// Separate component for each question card
const QuestionCard: React.FC<{
  question: SimilarQuestion;
  onUseExisting: (question: SimilarQuestion) => void;
  onAddAsVariation: (question: SimilarQuestion) => void;
  isProcessing: boolean;
  getConfidenceIcon: (level: string) => React.ReactNode;
  getConfidenceMessage: (level: string, similarity: number) => string;
  getSimilarityBadgeColor: (level: string) => string;
}> = ({
  question,
  onUseExisting,
  onAddAsVariation,
  isProcessing,
  getConfidenceIcon,
  getConfidenceMessage,
  getSimilarityBadgeColor,
}) => {
  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      <div className="space-y-2">
        {/* Similarity Badge and Type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getConfidenceIcon(question.confidenceLevel)}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSimilarityBadgeColor(question.confidenceLevel)}`}>
              {getConfidenceMessage(question.confidenceLevel, question.similarity)}
            </span>
          </div>
          <span className="text-xs text-gray-500 uppercase">{question.type}</span>
        </div>

        {/* Question Text */}
        <div>
          <p className="text-sm font-medium text-gray-900">{question.question}</p>
          {question.parentQuestion && (
            <p className="text-xs text-gray-500 mt-1">
              Variation of: "{question.parentQuestion}"
            </p>
          )}
        </div>

        {/* Answer Preview */}
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-600 line-clamp-2">{question.answer}</p>
        </div>

        {/* Similarity Details */}
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700">Match details</summary>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div>Text similarity: {question.similarityDetails.levenshtein}%</div>
            <div>N-gram match: {question.similarityDetails.ngram}%</div>
            <div>Token match: {question.similarityDetails.token}%</div>
            <div>Word order: {question.similarityDetails.wordOrder}%</div>
          </div>
        </details>

        {/* Variations Count */}
        {question.isOriginal && question.variations && question.variations.length > 0 && (
          <p className="text-xs text-gray-500">
            Has {question.variations.length} variation{question.variations.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => onUseExisting(question)}
            disabled={isProcessing}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Use This (Mark as Processed)
          </button>
          {question.isOriginal && (
            <button
              onClick={() => onAddAsVariation(question)}
              disabled={isProcessing}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add as Variation
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimilarityCheckStep;